<template>
	<DialogControl v-model="open" fullscreen :title="title" :subtitle="subtitle" icon="inventory_2" :attach="overlayTarget" divided>
		<template v-if="entry">
			<PromoNegoRow v-if="nego" :key="entry.key" stacked :campaign="campaign" :offer="offer" :offer-ref="offerRef" :product-id="entry.productId" :free="entry.free" />
			<PromoStatementRow v-else :key="entry.key" stacked :campaign="campaign" :offer="offer" :offer-ref="offerRef" :product-id="entry.productId" :free="entry.free" />
		</template>
		<template #actions>
			<div class="promo-product-sheet__footer">
				<span />
				<div class="d-flex align-center ga-1">
					<v-btn icon="chevron_left" variant="text" :disabled="index <= 0" :title="t('PreviousProduct')" @click="index -= 1" />
					<span class="text-body-small text-medium-emphasis">{{ index + 1 }} / {{ entries.length }}</span>
					<v-btn icon="chevron_right" variant="text" :disabled="index >= entries.length - 1" :title="t('NextProduct')" @click="index += 1" />
				</div>
				<div class="d-flex align-center ga-1">
					<v-btn v-if="removable" icon="delete" variant="text" color="error" :title="t('Delete')" :disabled="entry.free && !editable" @click="remove" />
					<v-btn color="primary" variant="tonal" @click="open = false">{{ t('Close') }}</v-btn>
				</div>
			</div>
		</template>
	</DialogControl>
</template>

<script setup>
import { computed, inject, watch } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { getProduct } from '@/utils/domain/promo/promoModel';
import { isNegoPhase } from '@/utils/domain/promo/promoPhase';
import { isProductEditable } from '@/utils/domain/promo/promoNego';
import PromoNegoRow from './PromoNegoRow.vue';
import PromoStatementRow from './PromoStatementRow.vue';

const open = defineModel({ type: Boolean, default: false });
const index = defineModel('index', { type: Number, default: 0 });
const props = defineProps({
	entries: { type: Array, required: true },
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	phase: { type: String, required: true },
});

const { snapshot, ops, t } = usePromoStep();
const overlayTarget = inject('overlayTarget', null);

const nego = computed(() => isNegoPhase(props.phase));
const entry = computed(() => props.entries[index.value] || null);
const product = computed(() => (entry.value ? getProduct(snapshot.value, entry.value.productId) : null));
const title = computed(() => product.value?.label || '');
const subtitle = computed(() => [entry.value?.free ? t('PromoFree') : null, product.value?.ean].filter(Boolean).join(' · '));
const editable = computed(() => !!entry.value && isProductEditable(snapshot.value, props.campaign, props.offerRef, entry.value.productId));
const removable = computed(() => nego.value && !!entry.value && (entry.value.free || (!!props.offerRef.offerKey && !snapshot.value.readonly)));

function remove() {
	if (entry.value.free) ops.removeFree(props.offerRef, entry.value.productId);
	else ops.removeLocalProduct(props.offerRef, entry.value.productId);
}

watch(
	() => props.entries.length,
	(length) => {
		if (index.value > length - 1) index.value = Math.max(length - 1, 0);
		if (!length) open.value = false;
	}
);
</script>
